import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Avatar from '@mui/material/Avatar';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import StoreTabPanel from '../components/common/StoreTabPanel';
import ImageGallery from '../components/common/ImageGallery';
import CollapsibleCard, { CollapseAllContext } from '../components/common/CollapsibleCard';
import TagsInput from '../components/common/TagsInput';
import FieldEditor from '../components/common/FieldEditor';
import DimensionsEditor from '../components/common/DimensionsEditor';
import PricingEditor, { flattenPricing } from '../components/common/PricingEditor';
import ExtraInfoEditor, { normalizeMiddleToEntries } from '../components/common/ExtraInfoEditor';
import ThreeDModelsEditor from '../components/common/ThreeDModelsEditor';
import ProductConfiguratorSetup from '../components/product/ProductConfiguratorSetup';
import CopyFromProductDialog from '../components/common/CopyFromProductDialog';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useStores } from '../context/StoresContext';
import { useAuth } from '../context/AuthContext';
import { getProduct, updateProduct, setOverride, setMetafieldValue, updateProductLinks, updateProductVariants, updateVariantFieldValues, updateAdditionalOptions, searchShopifyProducts, matchProduct as matchProductApi, unmatchProduct as unmatchProductApi, updateShopifyStatus, getProductTypes } from '../services/productsApi';
import { getFields } from '../services/fieldsApi';
import { getFabricPatterns, getFabricCollections } from '../services/fabricsApi';
import { getFinishTypes } from '../services/finishesApi';
import { getCertifications } from '../services/certificationsApi';
import { getInstallations } from '../services/installationsApi';
import { getProducts, getFamilyIds, getShopifyImages } from '../services/productsApi';
import { getCurrencies } from '../services/currenciesApi';
import { getMaterials } from '../services/materialsApi';
import { getManufacturers } from '../services/manufacturersApi';
import { getCalculations } from '../services/calculationsApi';
import { getFabricGrades } from '../services/fabricGradesApi';
import { getSpecifications } from '../services/specificationsApi';
import { getCountries } from '../services/countriesApi';
import { syncProduct, pushProduct, syncProductFabrics } from '../services/syncApi';
import { getOptionCategories } from '../services/optionsApi';
import { getFeatures } from '../services/featuresApi';
import { getSettings } from '../services/settingsApi';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeStores } = useStores();
  const { canViewPrice } = useAuth();

  const [product, setProduct] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushStoreSel, setPushStoreSel] = useState(new Set());
  const [postSavePromptOpen, setPostSavePromptOpen] = useState(false);
  // "Copy from another product" dialogs for the Dimensions and Pricing cards.
  // Each dialog reuses the same component; the difference is which field of
  // the source product we extract (dimensions vs pricing).
  const [copyDimensionsOpen, setCopyDimensionsOpen] = useState(false);
  const [copyPricingOpen, setCopyPricingOpen] = useState(false);

  // Editable state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [images, setImages] = useState([]);
  const [threeDModels, setThreeDModels] = useState([]);
  // Storefront 3D configurator config: { enabled, modelUrl, format, mappings }
  const [configurator, setConfigurator] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [inventoryTracking, setInventoryTracking] = useState('');
  const [productType, setProductType] = useState('');
  const [leadTime, setLeadTime] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [productDimensions, setProductDimensions] = useState({});
  const [productPricing, setProductPricing] = useState({ groups: [] });
  const [pricingUpdatedAt, setPricingUpdatedAt] = useState('');
  // Snapshot of the pricing JSON as it was on initial fetch. Any divergence
  // from this baseline auto-stamps pricingUpdatedAt to today (the UI promises
  // "Auto-stamps to today when the pricing table changes").
  const loadedPricingRef = useRef(null);
  const [productManufacturerId, setProductManufacturerId] = useState('');
  const [moq, setMoq] = useState('');
  const [stackable, setStackable] = useState('');
  const [sku, setSku] = useState('');
  const [productSku, setProductSku] = useState('');
  const [productStatus, setProductStatus] = useState('ready');
  const [internalNote, setInternalNote] = useState('');
  const [additionalTags, setAdditionalTags] = useState([]);
  // Per-product opt-out of bulk fabric/finish linking (Bulk Operations page).
  const [excludeFabricsFromBulk, setExcludeFabricsFromBulk] = useState(false);
  const [excludeFinishesFromBulk, setExcludeFinishesFromBulk] = useState(false);
  const [extraInfoRight, setExtraInfoRight] = useState({ items: [] });
  const [extraInfoMiddle, setExtraInfoMiddle] = useState({ entries: [] });
  const [variants, setVariants] = useState([]);
  const [variantGroups, setVariantGroups] = useState([]);
  const [variantSubGroups, setVariantSubGroups] = useState([]);
  const [variantSizes, setVariantSizes] = useState([]);
  const [variantFieldVals, setVariantFieldVals] = useState({}); // { "variantId-fieldId": value }
  const [additionalOptions, setAdditionalOptions] = useState([]);

  // Store matching & status
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchStoreId, setMatchStoreId] = useState(null);
  const [matchQuery, setMatchQuery] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [matchSearching, setMatchSearching] = useState(false);
  const [matchingProduct, setMatchingProduct] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Option categories (admin-defined groups of options w/ images)
  const [allOptionCategories, setAllOptionCategories] = useState([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);

  // Finish group filter (for bulk add)
  const [finishGroupFilter, setFinishGroupFilter] = useState('');

  // Multi-select link options
  const [allFabricPatterns, setAllFabricPatterns] = useState([]);
  const [allFabricCollections, setAllFabricCollections] = useState([]);
  // Broadcast "collapse all / expand all" to every CollapsibleCard on the page.
  const [collapseSignal, setCollapseSignal] = useState({ signal: 0, open: false });
  const collapseAllSections = (open) => setCollapseSignal((s) => ({ signal: s.signal + 1, open }));
  const [allFinishTypes, setAllFinishTypes] = useState([]);
  const [allCertifications, setAllCertifications] = useState([]);
  const [allInstallations, setAllInstallations] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [allCalculations, setAllCalculations] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [allSpecifications, setAllSpecifications] = useState([]);
  const [allFamilyIds, setAllFamilyIds] = useState([]);
  const [allProductTypes, setAllProductTypes] = useState([]);
  const [allFabricGrades, setAllFabricGrades] = useState([]);
  const [fabricMfgFilter, setFabricMfgFilter] = useState('');
  const [finishMfgFilter, setFinishMfgFilter] = useState('');

  // Multi-select link selections (arrays of IDs)
  const [selectedFabricPatterns, setSelectedFabricPatterns] = useState([]);
  // Fabric TYPES (groups) linked to the product. Linking a group auto-includes
  // every pattern/color under it — new ones added later just need a push.
  const [selectedFabricTypes, setSelectedFabricTypes] = useState([]);
  // Curated fabric collections linked to the product (auto-include their patterns).
  const [selectedFabricCollections, setSelectedFabricCollections] = useState([]);
  const [selectedFinishTypes, setSelectedFinishTypes] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [selectedInstallations, setSelectedInstallations] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [allSettings, setAllSettings] = useState([]);
  const [selectedSettings, setSelectedSettings] = useState([]);
  const [selectedSpecifications, setSelectedSpecifications] = useState([]);
  const [specValues, setSpecValues] = useState({}); // { specId: { rowIdx: { value: '', additional_text: '' } } }

  // Per-store overrides: { storeId: { field_name: value } }
  const [overrides, setOverrides] = useState({});

  // Metafield values: { fieldId: { default: value, storeId: value } }
  const [metafields, setMetafields] = useState({});

  // Unsaved-changes detection. We snapshot every editable field as a JSON
  // string; the baseline is captured after fetchData lands. Anything diverging
  // from the baseline flips isDirty → true and the guard hook blocks navigation
  // (in-app via useBlocker, tab close via beforeunload).
  const editableSnapshot = JSON.stringify({
    title, description, tags, images, threeDModels, inventoryTracking, productType, leadTime,
    familyId, productDimensions, productPricing, pricingUpdatedAt, productManufacturerId, moq, internalNote,
    stackable, sku, productSku, productStatus, additionalTags, extraInfoRight,
    extraInfoMiddle, variants, variantGroups, variantSubGroups, variantSizes,
    variantFieldVals, additionalOptions, selectedOptionIds,
    selectedFabricPatterns, selectedFabricTypes, selectedFabricCollections, selectedFinishTypes, selectedCertifications,
    selectedInstallations, selectedMaterials, selectedFamilyMembers,
    selectedFeatures, selectedSettings, selectedSpecifications, specValues,
    overrides, metafields, excludeFabricsFromBulk, excludeFinishesFromBulk, configurator,
  });
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  useEffect(() => {
    if (!loading && savedSnapshot === null) {
      setSavedSnapshot(editableSnapshot);
    }
  }, [loading, savedSnapshot, editableSnapshot]);
  const isDirty = savedSnapshot !== null && editableSnapshot !== savedSnapshot;
  useUnsavedChangesGuard(isDirty);

  // Parallel snapshot of only the fields that flow to Shopify. Used to decide
  // whether the post-save "Push to stores?" prompt should appear — editing
  // internal_note or flipping product_status shouldn't ask to push, those
  // are dashboard-only flags.
  const shopifyAffectingSnapshot = JSON.stringify({
    title, description, tags, images, threeDModels, inventoryTracking, productType, leadTime,
    familyId, productDimensions, productPricing, pricingUpdatedAt, productManufacturerId, moq,
    stackable, sku, productSku, additionalTags, extraInfoRight,
    extraInfoMiddle, variants, variantGroups, variantSubGroups, variantSizes,
    variantFieldVals, additionalOptions, selectedOptionIds,
    selectedFabricPatterns, selectedFabricTypes, selectedFabricCollections, selectedFinishTypes, selectedCertifications,
    selectedInstallations, selectedMaterials, selectedFamilyMembers,
    selectedFeatures, selectedSettings, selectedSpecifications, specValues,
    overrides, metafields,
  });
  const [savedShopifySnapshot, setSavedShopifySnapshot] = useState(null);
  useEffect(() => {
    if (!loading && savedShopifySnapshot === null) {
      setSavedShopifySnapshot(shopifyAffectingSnapshot);
    }
  }, [loading, savedShopifySnapshot, shopifyAffectingSnapshot]);

  // Auto-stamp pricing date when the pricing JSON diverges from the fetched
  // baseline. Skip until loadedPricingRef is populated by fetchData so the
  // initial setProductPricing call doesn't trigger a false stamp. The user
  // can still override the date manually after editing.
  useEffect(() => {
    if (loadedPricingRef.current === null) return;
    const currentJson = JSON.stringify(productPricing || { groups: [] });
    if (currentJson !== loadedPricingRef.current) {
      const today = new Date().toISOString().slice(0, 10);
      setPricingUpdatedAt((prev) => (prev === today ? prev : today));
    }
  }, [productPricing]);

  const fetchData = useCallback(async () => {
    try {
      const [productData, fieldsData, patternsData, typesData, certsData, instData, prodsData, currenciesData, materialsData, manufacturersData, calcsData, countriesData, specsData, optionCatsData, featuresData, settingsData, familyIdsData, productTypesData, fabricGradesData, fabricCollectionsData] = await Promise.all([
        getProduct(id),
        getFields(),
        getFabricPatterns(),
        getFinishTypes(),
        getCertifications(),
        getInstallations(),
        getProducts(1, 100),
        getCurrencies(),
        getMaterials(),
        getManufacturers(),
        getCalculations(),
        getCountries(),
        getSpecifications(),
        getOptionCategories(),
        getFeatures(),
        getSettings(),
        getFamilyIds(),
        getProductTypes(),
        getFabricGrades(),
        getFabricCollections(),
      ]);

      const p = productData.product;
      setProduct(p);
      setTitle(p.title);
      setDescription(p.description || '');
      setTags(p.tags || []);
      setImages(p.images || []);
      setThreeDModels(Array.isArray(p.three_d_models) ? p.three_d_models : []);
      setConfigurator(p.configurator || null);
      setFields(fieldsData.fields);
      setCurrencies(currenciesData.items || []);
      setInventoryTracking(p.inventory_tracking || '');
      setProductType(p.product_type || '');
      setLeadTime(p.lead_time || '');
      setFamilyId(p.family_id || '');
      setProductDimensions(p.dimensions || {});
      const initialPricing = p.pricing && p.pricing.groups ? p.pricing : { groups: [] };
      setProductPricing(initialPricing);
      loadedPricingRef.current = JSON.stringify(initialPricing);
      // pricing_updated_at comes back as ISO timestamp; collapse to YYYY-MM-DD
      // for the <input type="date"> control. Empty string means "no value yet".
      setPricingUpdatedAt(p.pricing_updated_at ? String(p.pricing_updated_at).slice(0, 10) : '');
      setProductManufacturerId(p.manufacturer_id || '');
      setMoq(p.moq || '');
      setStackable(p.stackable || '');
      setSku(p.identifier_value || '');
      setProductSku(p.sku || '');
      setProductStatus(p.product_status || 'ready');
      setInternalNote(p.internal_note || '');
      setAdditionalTags(p.additional_tags || []);
      setExcludeFabricsFromBulk(p.exclude_fabrics_from_bulk || false);
      setExcludeFinishesFromBulk(p.exclude_finishes_from_bulk || false);
      setExtraInfoRight((p.extra_info && p.extra_info.right) || { items: [] });
      setExtraInfoMiddle(normalizeMiddleToEntries(p.extra_info && p.extra_info.middle));
      setAllOptionCategories(optionCatsData.items || []);
      setSelectedOptionIds((p.options || []).map((o) => o.id));
      setVariants(p.variants || []);
      setVariantGroups(p.variant_groups || []);
      setVariantSubGroups(p.variant_sub_groups || []);
      setVariantSizes(p.variant_sizes || []);
      setAdditionalOptions(p.additional_options || []);

      // Build variant field values map
      const vfvMap = {};
      for (const vfv of (p.variant_field_values || [])) {
        const key = `${vfv.variant_id}-${vfv.field_id}`;
        vfvMap[key] = vfv.value;
      }
      setVariantFieldVals(vfvMap);

      setAllFabricPatterns(patternsData.items);
      setAllFabricCollections(fabricCollectionsData.items || []);
      setAllFinishTypes(typesData.items);
      setAllCertifications(certsData.items || certsData.certifications || []);
      setAllInstallations(instData.items);
      setAllProducts(prodsData.products.filter((pr) => pr.id !== parseInt(id)));
      setAllMaterials(materialsData.items || []);
      setAllManufacturers(manufacturersData.items || []);
      setAllCalculations(calcsData.items || []);
      setAllCountries(countriesData.items || []);
      setAllSpecifications(specsData || []);
      setAllFeatures(featuresData.items || []);
      setAllSettings(settingsData.items || []);
      setAllFamilyIds(familyIdsData.items || []);
      setAllProductTypes(Array.isArray(productTypesData) ? productTypesData : []);
      setAllFabricGrades(fabricGradesData.items || []);

      // Set selected links from product data
      setSelectedFabricPatterns((p.fabric_patterns || []).map((fp) => fp.id));
      setSelectedFabricTypes((p.fabric_types || []).map((ft) => ft.id));
      setSelectedFabricCollections((p.fabric_collections || []).map((c) => c.id));
      setSelectedFinishTypes((p.finish_types || []).map((ft) => ft.id));
      setSelectedCertifications((p.certifications || []).map((c) => c.id));
      setSelectedInstallations((p.installations || []).map((i) => i.id));
      setSelectedMaterials((p.materials || []).map((m) => m.id));
      setSelectedFamilyMembers((p.family_members || []).map((fm) => fm.id));
      setSelectedFeatures((p.features || []).map((f) => f.id));
      setSelectedSettings((p.settings || []).map((s) => s.id));
      setSelectedSpecifications((p.specifications || []).map((s) => s.id));
      setSpecValues(p.spec_values || {});

      // Build overrides map
      const overrideMap = {};
      for (const o of p.overrides || []) {
        if (!overrideMap[o.store_id]) overrideMap[o.store_id] = {};
        overrideMap[o.store_id][o.field_name] = o.value;
      }
      setOverrides(overrideMap);

      // Build metafields map
      const metaMap = {};
      for (const m of p.metafield_values || []) {
        if (!metaMap[m.field_id]) metaMap[m.field_id] = {};
        const key = m.store_id ? String(m.store_id) : 'default';
        metaMap[m.field_id][key] = m.value;
      }
      setMetafields(metaMap);
    } catch (err) {
      console.error('Failed to fetch product:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Override helpers for core fields
  const getStoreOverrides = (fieldName) => {
    const result = {};
    for (const [storeId, storeFields] of Object.entries(overrides)) {
      if (storeFields[fieldName] !== undefined) {
        result[storeId] = storeFields[fieldName];
      }
    }
    return result;
  };

  const handleOverrideChange = (fieldName, storeId, value) => {
    setOverrides((prev) => {
      const updated = { ...prev };
      if (value === undefined) {
        if (updated[storeId]) {
          const { [fieldName]: _, ...rest } = updated[storeId];
          if (Object.keys(rest).length === 0) {
            delete updated[storeId];
          } else {
            updated[storeId] = rest;
          }
        }
      } else {
        if (!updated[storeId]) updated[storeId] = {};
        updated[storeId] = { ...updated[storeId], [fieldName]: value };
      }
      return updated;
    });
  };

  // Metafield helpers
  const getMetafieldStoreOverrides = (fieldId) => {
    const result = {};
    const fieldMeta = metafields[fieldId] || {};
    for (const [key, val] of Object.entries(fieldMeta)) {
      if (key !== 'default') {
        result[key] = val;
      }
    }
    return result;
  };

  const handleMetafieldChange = (fieldId, storeId, value) => {
    setMetafields((prev) => {
      const updated = { ...prev };
      if (!updated[fieldId]) updated[fieldId] = {};
      const key = storeId ? String(storeId) : 'default';
      if (value === undefined) {
        delete updated[fieldId][key];
      } else {
        updated[fieldId] = { ...updated[fieldId], [key]: value };
      }
      return updated;
    });
  };

  // Measurement unit conversion
  const UNIT_CONVERSIONS = {
    yards: { to: 'meters', factor: 0.9144, label: 'Yards', pairLabel: 'Meters' },
    meters: { to: 'yards', factor: 1.09361, label: 'Meters', pairLabel: 'Yards' },
    sq_feet: { to: 'sq_meters', factor: 0.092903, label: 'Sq Feet', pairLabel: 'Sq Mtr' },
    sq_meters: { to: 'sq_feet', factor: 10.7639, label: 'Sq Mtr', pairLabel: 'Sq Feet' },
    lbs: { to: 'kgs', factor: 0.453592, label: 'Lbs', pairLabel: 'Kgs' },
    kgs: { to: 'lbs', factor: 2.20462, label: 'Kgs', pairLabel: 'Lbs' },
    inches: { to: 'cm', factor: 2.54, label: 'Inches', pairLabel: 'CM' },
    cm: { to: 'inches', factor: 0.393701, label: 'CM', pairLabel: 'Inches' },
  };

  const convertUnit = (value, unit) => {
    const conv = UNIT_CONVERSIONS[unit];
    if (!conv || !value || isNaN(parseFloat(value))) return null;
    return (parseFloat(value) * conv.factor).toFixed(2);
  };

  // Variant group handlers (Level 1)
  const handleAddGroup = () => {
    setVariantGroups((prev) => [...prev, { name: '', description: '', _tempId: Date.now() }]);
  };

  const handleGroupChange = (index, field, value) => {
    setVariantGroups((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveGroup = (index) => {
    const group = variantGroups[index];
    const groupId = group.id || group._tempId;
    // Cascade: remove sub-groups, sizes, and variants
    const subGroupIds = new Set(variantSubGroups.filter((sg) => sg.variant_group_id === groupId).map((sg) => sg.id || sg._tempId));
    const sizeIds = new Set(variantSizes.filter((sz) => subGroupIds.has(sz.variant_sub_group_id)).map((sz) => sz.id || sz._tempId));
    setVariants((prev) => prev.filter((v) => v.variant_group_id !== groupId && !sizeIds.has(v.variant_size_id)));
    setVariantSizes((prev) => prev.filter((sz) => !subGroupIds.has(sz.variant_sub_group_id)));
    setVariantSubGroups((prev) => prev.filter((sg) => sg.variant_group_id !== groupId));
    setVariantGroups((prev) => prev.filter((_, i) => i !== index));
  };

  // Sub-group handlers (Level 2)
  const handleAddSubGroup = (groupId) => {
    setVariantSubGroups((prev) => [...prev, { name: '', variant_group_id: groupId, _tempId: Date.now() }]);
  };

  const handleSubGroupChange = (index, field, value) => {
    setVariantSubGroups((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveSubGroup = (sgIndex) => {
    const sg = variantSubGroups[sgIndex];
    const sgId = sg.id || sg._tempId;
    const sizeIds = new Set(variantSizes.filter((sz) => sz.variant_sub_group_id === sgId).map((sz) => sz.id || sz._tempId));
    setVariants((prev) => prev.filter((v) => !sizeIds.has(v.variant_size_id)));
    setVariantSizes((prev) => prev.filter((sz) => sz.variant_sub_group_id !== sgId));
    setVariantSubGroups((prev) => prev.filter((_, i) => i !== sgIndex));
  };

  // Size handlers (Level 3)
  const handleAddSize = (subGroupId) => {
    setVariantSizes((prev) => [...prev, { name: '', variant_sub_group_id: subGroupId, _tempId: Date.now() }]);
  };

  const handleSizeChange = (index, field, value) => {
    setVariantSizes((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveSize = (szIndex) => {
    const sz = variantSizes[szIndex];
    const szId = sz.id || sz._tempId;
    setVariants((prev) => prev.filter((v) => v.variant_size_id !== szId));
    setVariantSizes((prev) => prev.filter((_, i) => i !== szIndex));
  };

  // Variant handlers (Level 4 — material rows with price)
  const handleAddVariantToSize = (sizeId, groupId) => {
    setVariants((prev) => [...prev, { name: '', mfg_price: 0, variant_size_id: sizeId, variant_group_id: groupId }]);
  };

  const handleAddVariantToGroup = (groupId) => {
    setVariants((prev) => [...prev, { name: '', mfg_price: 0, variant_group_id: groupId }]);
  };

  const handleVariantFieldValChange = (variantId, fieldId, value) => {
    setVariantFieldVals((prev) => ({ ...prev, [`${variantId}-${fieldId}`]: value }));
  };

  // Store matching handlers
  const openMatchDialog = (storeId) => {
    setMatchStoreId(storeId);
    setMatchQuery('');
    setMatchResults([]);
    setMatchDialogOpen(true);
  };

  const handleMatchSearch = async () => {
    if (!matchQuery.trim() || !matchStoreId) return;
    setMatchSearching(true);
    try {
      const data = await searchShopifyProducts(id, matchStoreId, matchQuery);
      setMatchResults(data.products || []);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setMatchSearching(false);
    }
  };

  const handleMatch = async (shopifyProductId) => {
    setMatchingProduct(true);
    try {
      await matchProductApi(id, matchStoreId, shopifyProductId);
      setMatchDialogOpen(false);
      await fetchData();
    } catch (err) {
      console.error('Match failed:', err);
    } finally {
      setMatchingProduct(false);
    }
  };

  const handleUnmatch = async (storeId) => {
    if (!window.confirm('Unlink this product from this store?')) return;
    try {
      await unmatchProductApi(id, storeId);
      await fetchData();
    } catch (err) {
      console.error('Unmatch failed:', err);
    }
  };

  const handleStatusChange = async (storeId, newStatus) => {
    setUpdatingStatus(storeId);
    try {
      await updateShopifyStatus(id, storeId, newStatus);
      await fetchData();
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSave = async ({ showPushPrompt = true } = {}) => {
    setSaving(true);
    // Capture whether anything Shopify-affecting changed BEFORE we wipe the
    // baselines for the in-flight save. If only dashboard-only fields
    // (internal_note, product_status) changed, the post-save Push prompt
    // should stay closed — there's nothing to push to Shopify.
    const shopifyChangedThisSave =
      savedShopifySnapshot !== null && shopifyAffectingSnapshot !== savedShopifySnapshot;
    // Did the fabric links (patterns / groups / collections) change vs what was
    // loaded? If so, we auto-push the newly-linked fabrics to the product's live
    // stores after saving, so they appear without a manual push.
    const sig = (arr) => JSON.stringify([...(arr || [])].sort());
    const fabricLinksChanged =
      sig(selectedFabricPatterns) !== sig((product?.fabric_patterns || []).map((x) => x.id)) ||
      sig(selectedFabricTypes) !== sig((product?.fabric_types || []).map((x) => x.id)) ||
      sig(selectedFabricCollections) !== sig((product?.fabric_collections || []).map((x) => x.id));
    try {
      // Save default product fields
      await updateProduct(id, {
        title,
        description,
        tags,
        images,
        inventory_tracking: inventoryTracking,
        spec_values: specValues,
        product_type: productType,
        lead_time: leadTime,
        family_id: familyId,
        dimensions: productDimensions,
        pricing: productPricing,
        pricing_updated_at: pricingUpdatedAt || '',
        manufacturer_id: productManufacturerId || null,
        moq,
        stackable,
        additional_tags: additionalTags,
        extra_info: { right: extraInfoRight, middle: extraInfoMiddle },
        three_d_models: threeDModels,
        identifier_value: sku,
        sku: productSku,
        product_status: productStatus,
        internal_note: internalNote,
        exclude_fabrics_from_bulk: excludeFabricsFromBulk,
        exclude_finishes_from_bulk: excludeFinishesFromBulk,
        configurator,
      });

      // Save variant groups and variants
      const varResult = await updateProductVariants(id, variants, variantGroups, variantSubGroups, variantSizes);

      // Save variant field values (only for per_variant fields and saved variants)
      const perVarFields = fields.filter((f) => f.per_variant);
      if (perVarFields.length > 0 && varResult.variants?.length > 0) {
        const vfvPayload = [];
        for (const sv of varResult.variants) {
          for (const f of perVarFields) {
            const key = `${sv.id}-${f.id}`;
            const val = variantFieldVals[key];
            if (val !== undefined) {
              vfvPayload.push({ variant_id: sv.id, field_id: f.id, store_id: null, value: val });
            }
          }
        }
        if (vfvPayload.length > 0) {
          await updateVariantFieldValues(id, vfvPayload);
        }
      }

      // Save additional options
      await updateAdditionalOptions(id, additionalOptions);

      // Save multi-select links
      await updateProductLinks(id, {
        fabric_pattern_ids: selectedFabricPatterns,
        fabric_type_ids: selectedFabricTypes,
        fabric_collection_ids: selectedFabricCollections,
        finish_type_ids: selectedFinishTypes,
        certification_ids: selectedCertifications,
        installation_ids: selectedInstallations,
        family_member_ids: selectedFamilyMembers,
        material_ids: selectedMaterials,
        feature_ids: selectedFeatures,
        setting_ids: selectedSettings,
        specification_ids: selectedSpecifications,
        option_ids: selectedOptionIds,
      });

      // Save overrides
      for (const [storeId, storeFields] of Object.entries(overrides)) {
        for (const [fieldName, value] of Object.entries(storeFields)) {
          await setOverride(id, storeId, fieldName, value);
        }
      }

      // Remove overrides that were deleted
      if (product?.overrides) {
        for (const orig of product.overrides) {
          const current = overrides[orig.store_id]?.[orig.field_name];
          if (current === undefined) {
            await setOverride(id, orig.store_id, orig.field_name, null);
          }
        }
      }

      // Save metafield values
      for (const [fieldId, values] of Object.entries(metafields)) {
        if (fieldId.startsWith('_')) continue; // skip temp keys like _fabricJsonField
        for (const [key, val] of Object.entries(values)) {
          const storeId = key === 'default' ? null : Number(key);
          await setMetafieldValue(id, Number(fieldId), storeId, val);
        }
      }

      await fetchData();
      // Re-baseline AFTER the fresh server data lands so the snapshot matches
      // the server-normalized values. Resetting before fetchData (the old bug)
      // let the effect recapture mid-save against the still-edited state, so
      // isDirty never cleared once the server normalized anything.
      setSavedSnapshot(null);
      setSavedShopifySnapshot(null);
      // Only offer to push when something Shopify-relevant actually changed.
      // Saves that only flipped product_status or edited the internal_note
      // (dashboard-only fields) skip the prompt — there's nothing to push.
      if (showPushPrompt && shopifyChangedThisSave) {
        setPostSavePromptOpen(true);
      }
      // Auto-push newly-linked fabrics to the product's live stores, then sync,
      // so attaching a collection/group/pattern actually shows on the storefront.
      if (fabricLinksChanged) {
        try {
          const r = await syncProductFabrics(id);
          if (r?.stores) {
            setSyncResult({ severity: 'success', message: r.message || 'Pushing new fabrics to your stores…' });
          }
        } catch (err) {
          const detail = err.response?.data?.message || err.message;
          setSyncResult({ severity: 'warning', message: `Saved, but auto-push of fabrics didn't start (${detail}). You can Push manually.` });
        }
      }
      return true;
    } catch (err) {
      console.error('Failed to save product:', err);
      const firstErr = err.response?.data?.errors?.[0];
      const detail = firstErr
        ? `${firstErr.field ? `${firstErr.field}: ` : ''}${firstErr.message}`
        : (err.response?.data?.message || err.message || 'Save failed');
      setSyncResult({ severity: 'error', message: `Couldn't save: ${detail}` });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runSyncOrPush = async (mode, storeIds) => {
    if (isDirty) {
      const saved = await handleSave({ showPushPrompt: false });
      if (!saved) return;
    }
    setSyncing(true);
    setSyncResult(null);
    const verb = mode === 'push' ? 'Pushed' : 'Synced';
    const failVerb = mode === 'push' ? 'Push' : 'Sync';
    try {
      const data = mode === 'push' ? await pushProduct(id, storeIds) : await syncProduct(id, storeIds);
      const synced = (data.results || []).filter((r) => r.status === 'synced').length;
      const errored = (data.results || []).filter((r) => r.status === 'error');
      const logSuffix = data.log_id ? ` · Log #${data.log_id}` : '';
      if (errored.length === 0) {
        setSyncResult({
          severity: 'success',
          message: `${verb} to ${synced} store(s)${logSuffix}`,
        });
      } else {
        const errLines = errored.map((r) => `Store ${r.store_id}: ${r.error || 'error'}`).join('\n');
        setSyncResult({
          severity: 'warning',
          message: `${verb} to ${synced} store(s), ${errored.length} failed${logSuffix}\n\n${errLines}`,
        });
      }
      await fetchData();
      // Re-baseline after the push/sync reloads server state so the dirty
      // indicator settles instead of lingering on normalized values.
      setSavedSnapshot(null);
      setSavedShopifySnapshot(null);
    } catch (err) {
      const detail = err.response?.data?.message || err.message || `${failVerb} failed`;
      const log = err.response?.data?.log_id ? ` · Log #${err.response.data.log_id}` : '';
      setSyncResult({ severity: 'error', message: `${failVerb} failed${log}: ${detail}` });
    } finally {
      setSyncing(false);
    }
  };

  const handleSync = () => runSyncOrPush('sync');

  // Push opens a store picker first — nothing is pushed/created until the user
  // selects stores. Defaults to the stores the product is already matched to.
  const openPushDialog = () => {
    const matched = activeStores
      .filter((s) => product?.store_statuses?.find((x) => x.store_id === s.id)?.shopify_product_id)
      .map((s) => s.id);
    setPushStoreSel(new Set(matched.length ? matched : activeStores.map((s) => s.id)));
    setPushDialogOpen(true);
  };
  const confirmPush = () => {
    const ids = Array.from(pushStoreSel);
    setPushDialogOpen(false);
    if (ids.length) runSyncOrPush('push', ids);
  };

  // Filter fabric patterns by manufacturer
  const filteredFabricPatterns = useMemo(() => {
    if (!fabricMfgFilter) return allFabricPatterns;
    return allFabricPatterns.filter((fp) =>
      (fp.manufacturers || []).some((m) => m.id === fabricMfgFilter)
    );
  }, [allFabricPatterns, fabricMfgFilter]);

  // Filter finish types by manufacturer and/or group
  const filteredFinishTypes = useMemo(() => {
    return allFinishTypes.filter((ft) => {
      const mfgOk = !finishMfgFilter || (ft.manufacturers || []).some((m) => m.id === finishMfgFilter);
      const gid = ft.finish_group_id || ft.group_id;
      const groupOk = !finishGroupFilter || gid === finishGroupFilter || String(gid) === String(finishGroupFilter);
      return mfgOk && groupOk;
    });
  }, [allFinishTypes, finishMfgFilter, finishGroupFilter]);

  // Distinct fabric TYPES (groups) derived from the loaded patterns — options
  // for the group-level linking picker.
  const allFabricTypes = useMemo(() => {
    const map = new Map();
    for (const p of allFabricPatterns) {
      const tid = p.fabric_type_id;
      if (tid && !map.has(tid)) map.set(tid, { id: tid, name: p.type_name || 'Fabric' });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFabricPatterns]);

  // Fabrics/finishes actually linked to this product — fed to the 3D
  // configurator setup so the admin can restrict which appear per part.
  const linkedConfiguratorFabrics = useMemo(() => {
    const sel = new Set(selectedFabricPatterns);
    return allFabricPatterns
      .filter((p) => sel.has(p.id))
      .map((p) => ({ id: p.id, name: p.name, typeName: p.type_name || 'Fabric' }));
  }, [allFabricPatterns, selectedFabricPatterns]);
  const linkedConfiguratorFinishes = useMemo(() => {
    const sel = new Set(selectedFinishTypes);
    return allFinishTypes
      .filter((t) => sel.has(t.id))
      .map((t) => ({ id: t.id, name: t.name, groupName: t.group_name || 'Finishes' }));
  }, [allFinishTypes, selectedFinishTypes]);

  // Distinct finish groups for the filter dropdown
  const finishGroupOptions = useMemo(() => {
    const map = new Map();
    for (const ft of allFinishTypes) {
      const gid = ft.finish_group_id || ft.group_id;
      const gname = ft.group_name;
      if (gid && gname && !map.has(gid)) map.set(gid, { id: gid, name: gname });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFinishTypes]);

  // Evaluate a formula string with given variables
  const evalFormula = useCallback((formula, vars) => {
    try {
      const allowed = /^[\d\s.+\-*/()]+$/;
      let expr = formula;
      for (const [key, val] of Object.entries(vars)) {
        expr = expr.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), String(val));
      }
      if (!allowed.test(expr)) return null;
      // eslint-disable-next-line no-new-func
      const result = new Function(`"use strict"; return (${expr});`)();
      return typeof result === 'number' && isFinite(result) ? result : null;
    } catch {
      return null;
    }
  }, []);

  // Calculated list prices: pick ONE active calculation per (store, mfg) bucket
  // Precedence: product-specific > manufacturer-specific > all
  const activeCalculations = useMemo(() => {
    if (allCalculations.length === 0) return [];
    const productId = parseInt(id);

    // Filter to calcs that *can* apply to this product.
    // - product-specific: product must be in the calc's product list
    // - manufacturer-specific: product's manufacturer must be in the calc's mfg list
    // - all-products: applies to every product
    const applicable = allCalculations.filter((c) => {
      const pids = c.product_ids || [];
      if (pids.length > 0) return pids.includes(productId);
      const mfgIds = (c.manufacturers || []).map((m) => m.id);
      if (c.manufacturer_id) mfgIds.push(c.manufacturer_id);
      if (mfgIds.length > 0) return productManufacturerId && mfgIds.includes(productManufacturerId);
      return true;
    });

    // Bucket by (store_id || 'global') + (manufacturer_ids or manufacturer_id || 'all')
    const bucketed = new Map();
    for (const c of applicable) {
      const pids = c.product_ids || [];
      const mfgs = c.manufacturers || [];
      const hasMfg = mfgs.length > 0 || c.manufacturer_id;
      const scope = pids.length > 0 ? 3 : (hasMfg ? 2 : 1); // product=3 > mfg=2 > all=1
      const mfgKey = mfgs.length > 0 ? mfgs.map((m) => m.id).sort().join(',') : (c.manufacturer_id || 'any');
      const key = `${c.store_id || 'global'}::${mfgKey}`;
      const existing = bucketed.get(key);
      if (!existing || scope > existing._scope) {
        bucketed.set(key, { ...c, _scope: scope });
      }
    }
    return Array.from(bucketed.values());
  }, [allCalculations, id, productManufacturerId]);

  // Pick the single best (highest precedence) calculation for the inline list-price preview.
  // Precedence: higher _scope first (product > mfg > all), then prefer a calc without store_id
  // (global) so the preview matches what unstored stores would push.
  const primaryCalc = useMemo(() => {
    if (activeCalculations.length === 0) return null;
    const sorted = [...activeCalculations].sort((a, b) => {
      if ((b._scope || 0) !== (a._scope || 0)) return (b._scope || 0) - (a._scope || 0);
      if (!a.store_id && b.store_id) return -1;
      if (!b.store_id && a.store_id) return 1;
      return 0;
    });
    return sorted[0];
  }, [activeCalculations]);

  // Use the PRODUCT's manufacturer to look up conv_rate/discount/tariff/markup.
  // Falls back to the calculation's manufacturer if product has none set.
  const computePricingListPrice = useCallback((row) => {
    if (!primaryCalc) return null;
    const base = parseFloat(row.mfg_price) || 0;
    const addon = parseFloat(row.add_on) || 0;
    if (!base && !addon) return null;
    const mfgId = productManufacturerId || primaryCalc.manufacturer_id;
    const mfg = mfgId ? allManufacturers.find((m) => m.id === mfgId) : null;
    const country = mfg?.country_id ? allCountries.find((co) => co.id === mfg.country_id) : null;
    const currency = mfg?.currency_id ? currencies.find((c) => c.id === mfg.currency_id) : null;
    const convRate = currency ? parseFloat(currency.value) || 1 : 1;
    const vars = {
      exchange_rate: convRate,
      conv_rate: convRate,
      discount: mfg ? parseFloat(mfg.discount) || 1 : 1,
      tariff: country ? parseFloat(country.tariff) || 1 : 1,
      markup: mfg ? parseFloat(mfg.markup) || 1 : 1,
      mfg_price: base,
      add_on: addon,
    };
    return evalFormula(primaryCalc.formula, vars);
  }, [primaryCalc, productManufacturerId, allManufacturers, allCountries, currencies, evalFormula]);

  // Build rows from recursive pricing groups for the legacy "Calculated List Prices" table
  const pricingRows = useMemo(() => {
    const flat = flattenPricing(productPricing?.groups || []);
    return flat.map((r) => ({ label: r.label || 'Unnamed', price: r.mfg_price + r.add_on }));
  }, [productPricing]);

  // Flat list with computed list prices for the Preview table (includes additional options)
  const pricingPreview = useMemo(() => {
    const rows = flattenPricing(productPricing?.groups || [], computePricingListPrice);
    // Append additional options as preview rows
    for (const opt of additionalOptions) {
      if (!opt.name) continue;
      const basePrice = parseFloat(opt.price) || 0;
      const addOn = parseFloat(opt.add_on) || 0;
      const list = computePricingListPrice ? computePricingListPrice({ mfg_price: basePrice, add_on: addOn }) : null;
      rows.push({
        path: ['Additional Options', opt.name],
        label: `Additional Options / ${opt.name}`,
        mfg_price: basePrice,
        add_on: addOn,
        list_price: list,
      });
    }
    return rows;
  }, [productPricing, computePricingListPrice, additionalOptions]);

  // Graded prices for the product's manufacturer. Each grade has a net (mfg)
  // value; the list adjustment runs the primary calculation as if the grade
  // value were a row's mfg_price. Theme adds `list_price` to the chosen base
  // row to get the final price for a variant on that grade.
  const gradedPreview = useMemo(() => {
    if (!productManufacturerId || allFabricGrades.length === 0) return [];
    const rows = [];
    for (const grade of allFabricGrades) {
      const priceRow = (grade.prices || []).find((p) => p.manufacturer_id === productManufacturerId);
      const mfgPrice = parseFloat(priceRow?.price) || 0;
      if (mfgPrice <= 0) continue;
      const listAdj = computePricingListPrice ? computePricingListPrice({ mfg_price: mfgPrice, add_on: 0 }) : null;
      rows.push({
        grade: grade.name,
        mfg_price: mfgPrice,
        list_price: listAdj,
      });
    }
    return rows;
  }, [allFabricGrades, productManufacturerId, computePricingListPrice]);

  const calculatedPrices = useMemo(() => {
    if (activeCalculations.length === 0) return { rows: [] };
    const priceItems = pricingRows.length > 0
      ? pricingRows
      : variants.map((v) => ({ label: v.name || 'Unnamed variant', price: parseFloat(v.mfg_price) || 0 }));
    if (priceItems.length === 0) return { rows: [] };

    const rows = [];

    for (const calc of activeCalculations) {
      // Use product's manufacturer first, fall back to calculation's
      const mfgId = productManufacturerId || calc.manufacturer_id;
      const mfg = mfgId ? allManufacturers.find((m) => m.id === mfgId) : null;
      const country = mfg?.country_id ? allCountries.find((co) => co.id === mfg.country_id) : null;
      const currency = mfg?.currency_id ? currencies.find((c) => c.id === mfg.currency_id) : null;

      const cRate = currency ? parseFloat(currency.value) || 1 : 1;
      const vars = {
        exchange_rate: cRate,
        conv_rate: cRate,
        discount: mfg ? parseFloat(mfg.discount) || 1 : 1,
        tariff: country ? parseFloat(country.tariff) || 1 : 1,
        markup: mfg ? parseFloat(mfg.markup) || 1 : 1,
      };

      const storeName = storeLabel(calc) || null;
      const mfgName = calc.manufacturer_name || null;
      const calcLabel = `${calc.name}${storeName ? ` (${storeName})` : ''}${mfgName ? ` — ${mfgName}` : ''}`;

      for (const item of priceItems) {
        const allVars = { ...vars, mfg_price: item.price, add_on: 0 };
        const result = evalFormula(calc.formula, allVars);
        rows.push({
          calcName: calcLabel,
          itemLabel: item.label,
          basePrice: item.price,
          listPrice: result,
        });
      }
    }

    return { rows };
  }, [variants, pricingRows, activeCalculations, productManufacturerId, allManufacturers, allCountries, currencies, evalFormula]);

  const handleAddVariant = () => {
    setVariants((prev) => [...prev, { name: '', mfg_price: 0 }]);
  };

  const handleVariantChange = (index, field, value) => {
    setVariants((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveVariant = (index) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Product Details" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box>
        <PageHeader title="Product Not Found" />
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/products')}>
          Back to Products
        </Button>
      </Box>
    );
  }

  return (
   <CollapseAllContext.Provider value={collapseSignal}>
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBackRounded />}
          onClick={() => navigate('/products')}
          sx={{ color: 'text.secondary' }}
        >
          Products
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => collapseAllSections(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Collapse all
        </Button>
        <Button size="small" onClick={() => collapseAllSections(true)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Expand all
        </Button>
      </Box>

      <PageHeader
        title={product.title}
        subtitle={product.sku ? `SKU: ${product.sku} — ID: ${product.identifier_value}` : `ID: ${product.identifier_value}`}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Push: opens a store picker first so nothing is created/updated on
                a store the user didn't select. */}
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <CloudUploadRounded />}
              onClick={openPushDialog}
              disabled={syncing || saving}
            >
              Push to Stores
            </Button>
            {/* Sync: only update already-linked stores. Hidden when nothing needs syncing. */}
            {(product.store_statuses || []).some((s) => s.status === 'modified' || s.status === 'not_synced' || s.status === 'error') && (
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <SyncRounded />}
                onClick={handleSync}
                disabled={syncing || saving}
              >
                Sync to Stores
              </Button>
            )}
          </Box>
        }
      />

      {/* Floating Save Changes button — sticks to bottom-right while scrolling */}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 32 },
          bottom: { xs: 16, md: 32 },
          zIndex: (theme) => theme.zIndex.appBar + 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {isDirty && !saving && (
          <Chip
            label="Unsaved changes"
            color="warning"
            size="small"
            sx={{ boxShadow: 4, fontWeight: 600 }}
          />
        )}
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveRounded />}
          onClick={handleSave}
          disabled={saving || !isDirty}
          sx={{ boxShadow: 6, borderRadius: 999, px: 3 }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>

      {syncResult && (
        <Alert severity={syncResult.severity} onClose={() => setSyncResult(null)} sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {syncResult.message}
        </Alert>
      )}

      {/* ───────── SECTION: Name & Status ───────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Name & Status</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <StoreTabPanel
            label="Name"
            stores={activeStores}
            defaultValue={title}
            storeOverrides={getStoreOverrides('title')}
            onDefaultChange={(val) => setTitle(val)}
            onOverrideChange={(storeId, val) => handleOverrideChange('title', storeId, val)}
            renderEditor={(value, onChange, readOnly) => (
              <TextField size="small" value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={readOnly} fullWidth />
            )}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Status (Connection &amp; Store Status)
          </Typography>
          {activeStores.map((store) => {
            const link = product.store_statuses?.find((s) => s.store_id === store.id);
            const isLinked = !!link?.shopify_product_id;
            const shopifyStatus = link?.shopify_status || 'unknown';
            const statusColors = { active: 'success.main', draft: 'text.secondary', archived: 'error.main', unknown: 'text.disabled' };

            return (
              <Box key={store.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {storeLabel(store)}
                  </Typography>
                  {isLinked ? (
                    <Typography variant="caption" color="text.secondary">
                      Linked to Shopify #{link.shopify_product_id}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Not linked
                    </Typography>
                  )}
                </Box>
                {isLinked && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={shopifyStatus !== 'unknown' ? shopifyStatus : ''}
                      onChange={(e) => handleStatusChange(store.id, e.target.value)}
                      displayEmpty
                      disabled={updatingStatus === store.id}
                    >
                      <MenuItem value="" disabled><em>Status</em></MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <Chip
                  size="small"
                  label={link?.status === 'synced' ? 'Synced' : link?.status === 'modified' ? 'Modified' : link?.status === 'error' ? 'Error' : link?.status === 'unlinked' ? 'Unlinked' : 'Not Synced'}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    bgcolor: link?.status === 'synced' ? 'rgba(0,217,166,0.12)' : link?.status === 'modified' ? 'rgba(108,99,255,0.12)' : link?.status === 'error' ? 'rgba(239,68,68,0.12)' : link?.status === 'unlinked' ? 'rgba(148,163,184,0.18)' : 'rgba(255,152,0,0.12)',
                    color: link?.status === 'synced' ? '#00D9A6' : link?.status === 'modified' ? '#6C63FF' : link?.status === 'error' ? '#EF4444' : link?.status === 'unlinked' ? '#64748B' : '#FF9800',
                  }}
                />
                {isLinked ? (
                  <Button size="small" color="error" variant="outlined" onClick={() => handleUnmatch(store.id)}>
                    Unlink
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => openMatchDialog(store.id)}>
                    Match
                  </Button>
                )}
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* ───────── SECTION: Main Info ───────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Main Info</Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
            <Autocomplete
              size="small"
              freeSolo
              autoSelect
              options={allProductTypes}
              value={productType || ''}
              onChange={(_, val) => setProductType(val || '')}
              onInputChange={(_, val, reason) => {
                // Only update on user typing — `reset` fires on every option-list close
                // and would clobber the current value with ''.
                if (reason === 'input') setProductType(val || '');
              }}
              filterOptions={(options, state) => {
                const q = (state.inputValue || '').toLowerCase().trim();
                const filtered = q
                  ? options.filter((o) => o.toLowerCase().includes(q))
                  : options;
                if (q && !options.some((o) => o.toLowerCase() === q)) {
                  return [...filtered, `Add "${state.inputValue}"`];
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Type" placeholder="e.g. Trolley, Chair, Table" />
              )}
              renderOption={(props, option) => {
                const isAdd = option.startsWith('Add "');
                return (
                  <li {...props} key={option}>
                    {isAdd ? <em>{option}</em> : option}
                  </li>
                );
              }}
              getOptionLabel={(option) => {
                if (typeof option !== 'string') return '';
                const m = option.match(/^Add "(.*)"$/);
                return m ? m[1] : option;
              }}
              fullWidth
            />
            <TextField
              size="small"
              label="ID"
              value={sku}
              fullWidth
              helperText="Product identifier (slug)"
              disabled
            />
          </Box>
          <StoreTabPanel
            label="SKU"
            stores={activeStores}
            defaultValue={productSku}
            storeOverrides={getStoreOverrides('sku')}
            useDefaultInitially={false}
            onDefaultChange={(val) => setProductSku(val)}
            onOverrideChange={(storeId, val) => handleOverrideChange('sku', storeId, val)}
            renderEditor={(value, onChange, readOnly) => (
              <TextField
                size="small"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={readOnly}
                fullWidth
                placeholder="SKU"
              />
            )}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
            <Autocomplete
              size="small"
              options={allManufacturers}
              getOptionLabel={(o) => o?.name || ''}
              value={allManufacturers.find((m) => m.id === productManufacturerId) || null}
              onChange={(_, val) => setProductManufacturerId(val?.id || '')}
              renderInput={(params) => <TextField {...params} label="Manufacturer" />}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
            <TextField
              select
              size="small"
              label="Product Status"
              value={productStatus}
              onChange={(e) => setProductStatus(e.target.value)}
              fullWidth
            >
              <MenuItem value="ready">Ready</MenuItem>
              <MenuItem value="needs_attention">Needs Attention</MenuItem>
            </TextField>
          </Box>
          {/* Internal note only appears when the product is flagged Needs
              Attention. Plain text, not pushed to Shopify; shown as a tooltip
              on the chip in the products list so editors see "why" without
              opening the detail page. */}
          {productStatus === 'needs_attention' && (
            <TextField
              size="small"
              label="Internal note (why does this need attention?)"
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              helperText="Visible in the dashboard only. Hover the Needs Attention chip on the products list to see this."
            />
          )}
        </CardContent>
      </Card>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <StoreTabPanel
            label="Description"
            stores={activeStores}
            defaultValue={description}
            storeOverrides={getStoreOverrides('description')}
            onDefaultChange={(val) => setDescription(val)}
            onOverrideChange={(storeId, val) => handleOverrideChange('description', storeId, val)}
            renderEditor={(value, onChange, readOnly) => (
              <TextField multiline rows={4} size="small" value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={readOnly} fullWidth />
            )}
          />
        </CardContent>
      </Card>

      {/* ───────── SECTION: Image ───────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Image</Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <StoreTabPanel
            label="Images"
            stores={activeStores}
            defaultValue={images}
            storeOverrides={getStoreOverrides('images')}
            useDefaultInitially={false}
            onDefaultChange={(val) => setImages(val)}
            onOverrideChange={(storeId, val) => handleOverrideChange('images', storeId, val)}
            renderEditor={(value, onChange, readOnly) => (
              <ImageGallery
                images={value || []}
                onChange={onChange}
                readOnly={readOnly}
                onFetchFromShopify={() => getShopifyImages(id)}
              />
            )}
          />
        </CardContent>
      </Card>

      {/* ───────── SECTION: 3D Models ───────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>3D Models</Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <StoreTabPanel
            label="3D Models (.glb / .gltf / .obj / .fbx / .stl / .zip)"
            stores={activeStores}
            defaultValue={threeDModels}
            storeOverrides={getStoreOverrides('three_d_models')}
            useDefaultInitially={false}
            onDefaultChange={(val) => setThreeDModels(val)}
            onOverrideChange={(storeId, val) => handleOverrideChange('three_d_models', storeId, val)}
            renderEditor={(value, onChange, readOnly) => (
              <ThreeDModelsEditor value={value || []} onChange={onChange} readOnly={readOnly} />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Pushed to Shopify as <code>custom.product_3d_models</code> (json array of {'{url, filename, content_type, size}'}).
          </Typography>
        </CardContent>
      </Card>

      {/* ───────── SECTION: Storefront 3D Configurator ───────── */}
      <ProductConfiguratorSetup
        models={threeDModels}
        fabrics={linkedConfiguratorFabrics}
        finishes={linkedConfiguratorFinishes}
        value={configurator}
        onChange={setConfigurator}
      />

      {/* Variant Groups (4-level hierarchy) — hidden in favor of Pricing card */}
      <Card sx={{ mb: 3, display: 'none' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Variant Groups</Typography>
            <Button size="small" startIcon={<AddRounded />} onClick={handleAddGroup}>Add Level 1</Button>
          </Box>

          {variantGroups.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No variant groups yet. Add a level to start building the pricing hierarchy.
            </Typography>
          )}

          {variantGroups.map((group, gIdx) => {
            const groupId = group.id || group._tempId;
            const groupSubGroups = variantSubGroups.map((sg, idx) => ({ ...sg, _sgIdx: idx })).filter((sg) => sg.variant_group_id === groupId);

            return (
              <Box key={groupId} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField size="small" label="Level 1" value={group.name}
                      onChange={(e) => handleGroupChange(gIdx, 'name', e.target.value)} fullWidth sx={{ mb: 1 }} />
                    <TextField size="small" label="Description" value={group.description || ''} multiline rows={2}
                      onChange={(e) => handleGroupChange(gIdx, 'description', e.target.value)} fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
                    <Button size="small" startIcon={<AddRounded />} onClick={() => handleAddSubGroup(groupId)}>Level 2</Button>
                    <IconButton size="small" color="error" onClick={() => handleRemoveGroup(gIdx)}><DeleteRounded fontSize="small" /></IconButton>
                  </Box>
                </Box>

                {groupSubGroups.map((sg) => {
                  const sgId = sg.id || sg._tempId;
                  const sgSizes = variantSizes.map((sz, idx) => ({ ...sz, _szIdx: idx })).filter((sz) => sz.variant_sub_group_id === sgId);

                  return (
                    <Box key={sgId} sx={{ ml: 2, mt: 1.5, pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <TextField size="small" label="Level 2" value={sg.name} placeholder="e.g. Squares"
                          onChange={(e) => handleSubGroupChange(sg._sgIdx, 'name', e.target.value)} sx={{ flex: 1 }} />
                        <Button size="small" startIcon={<AddRounded />} onClick={() => handleAddSize(sgId)}>Level 3</Button>
                        <IconButton size="small" color="error" onClick={() => handleRemoveSubGroup(sg._sgIdx)}><DeleteRounded fontSize="small" /></IconButton>
                      </Box>

                      {sgSizes.map((sz) => {
                        const szId = sz.id || sz._tempId;
                        const sizeVariants = variants.map((v, idx) => ({ ...v, _idx: idx })).filter((v) => v.variant_size_id === szId);

                        return (
                          <Box key={szId} sx={{ ml: 2, mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'grey.300' }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                              <TextField size="small" label="Level 3" value={sz.name} placeholder="e.g. 24 x 24"
                                onChange={(e) => handleSizeChange(sz._szIdx, 'name', e.target.value)} sx={{ flex: 1 }} />
                              <Button size="small" startIcon={<AddRounded />} onClick={() => handleAddVariantToSize(szId, groupId)}>Level 4</Button>
                              <IconButton size="small" color="error" onClick={() => handleRemoveSize(sz._szIdx)}><DeleteRounded fontSize="small" /></IconButton>
                            </Box>
                            {sizeVariants.map((v) => (
                              <Box key={v.id || `new-${v._idx}`} sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2, mb: 0.5 }}>
                                <TextField size="small" label="Level 4" value={v.name} placeholder="e.g. Red Oak"
                                  onChange={(e) => handleVariantChange(v._idx, 'name', e.target.value)} sx={{ flex: 1 }} />
                                <TextField size="small" label="Price" type="number" value={v.mfg_price}
                                  onChange={(e) => handleVariantChange(v._idx, 'mfg_price', e.target.value)} sx={{ width: 140 }}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  inputProps={{ min: 0, step: '0.01' }} />
                                <IconButton size="small" color="error" onClick={() => handleRemoveVariant(v._idx)}><DeleteRounded fontSize="small" /></IconButton>
                              </Box>
                            ))}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* ───────── SECTION: Connected Products ───────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Connected Products</Typography>

      {/* Fabric Patterns */}
      <CollapsibleCard
        title="Fabric Patterns"
        right={
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={excludeFabricsFromBulk}
                onChange={(e) => setExcludeFabricsFromBulk(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Exclude from bulk operations</Typography>}
          />
        }
      >

          {/* Group-level linking: link a whole fabric group and every pattern/
              color under it is included automatically — new ones added later
              just need a push, no bulk operation. */}
          <Autocomplete
            multiple
            size="small"
            options={allFabricTypes}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={allFabricTypes.filter((t) => selectedFabricTypes.includes(t.id))}
            onChange={(_, val) => setSelectedFabricTypes(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Fabric Groups (include all patterns)" />}
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Linking a group includes every pattern &amp; color in it automatically. Add individual patterns below only for one-off cases.
          </Typography>

          {/* Curated collections — link a named set of patterns (managed on the
              Fabrics page). Same auto-include behaviour as groups. */}
          <Autocomplete
            multiple
            size="small"
            options={allFabricCollections}
            getOptionLabel={(o) => o.internal_reference ? `${o.name} (${o.internal_reference})` : o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={allFabricCollections.filter((c) => selectedFabricCollections.includes(c.id))}
            onChange={(_, val) => setSelectedFabricCollections(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Fabric Collections (curated groups)" />}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Collections are curated sets you manage on the Fabrics page. A pattern can be in several collections.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Manufacturer</InputLabel>
              <Select
                value={fabricMfgFilter}
                onChange={(e) => setFabricMfgFilter(e.target.value)}
                label="Filter by Manufacturer"
              >
                <MenuItem value="">
                  <em>All Manufacturers</em>
                </MenuItem>
                {allManufacturers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {fabricMfgFilter && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const ids = filteredFabricPatterns.map((fp) => fp.id);
                    setSelectedFabricPatterns((prev) => [...new Set([...prev, ...ids])]);
                  }}
                >
                  Add All ({filteredFabricPatterns.length})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    const ids = new Set(filteredFabricPatterns.map((fp) => fp.id));
                    setSelectedFabricPatterns((prev) => prev.filter((id) => !ids.has(id)));
                  }}
                >
                  Remove All
                </Button>
              </>
            )}
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={filteredFabricPatterns}
            getOptionLabel={(o) => `${o.type_name || ''} — ${o.name}${o.grade ? ` (${o.grade})` : ''}`}
            value={allFabricPatterns.filter((fp) => selectedFabricPatterns.includes(fp.id))}
            onChange={(_, val) => setSelectedFabricPatterns(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Fabric Patterns" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={`${option.type_name || ''} — ${option.name}`}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
      </CollapsibleCard>

      {/* Finish Types */}
      <CollapsibleCard
        title="Finish Types"
        mb={3}
        right={
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={excludeFinishesFromBulk}
                onChange={(e) => setExcludeFinishesFromBulk(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Exclude from bulk operations</Typography>}
          />
        }
      >
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Manufacturer</InputLabel>
              <Select
                value={finishMfgFilter}
                onChange={(e) => setFinishMfgFilter(e.target.value)}
                label="Filter by Manufacturer"
              >
                <MenuItem value=""><em>All Manufacturers</em></MenuItem>
                {allManufacturers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Group</InputLabel>
              <Select
                value={finishGroupFilter}
                onChange={(e) => setFinishGroupFilter(e.target.value)}
                label="Filter by Group"
              >
                <MenuItem value=""><em>All Groups</em></MenuItem>
                {finishGroupOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(finishMfgFilter || finishGroupFilter) && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const ids = filteredFinishTypes.map((ft) => ft.id);
                    setSelectedFinishTypes((prev) => [...new Set([...prev, ...ids])]);
                  }}
                >
                  Add All ({filteredFinishTypes.length})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    const ids = new Set(filteredFinishTypes.map((ft) => ft.id));
                    setSelectedFinishTypes((prev) => prev.filter((id) => !ids.has(id)));
                  }}
                >
                  Remove All
                </Button>
              </>
            )}
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={filteredFinishTypes}
            getOptionLabel={(o) => `${o.group_name || ''} — ${o.name}${o.internal_reference ? ` · ${o.internal_reference}` : ''}`}
            filterOptions={(options, state) => {
              const q = state.inputValue.toLowerCase().trim();
              if (!q) return options;
              return options.filter((o) =>
                `${o.group_name || ''} ${o.name || ''} ${o.internal_reference || ''}`.toLowerCase().includes(q)
              );
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={allFinishTypes.filter((ft) => selectedFinishTypes.includes(ft.id))}
            onChange={(_, val) => setSelectedFinishTypes(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Finish Types (search by name or internal reference)" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={`${option.group_name || ''} — ${option.name}`}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
      </CollapsibleCard>

      {/* Family Members */}
      <CollapsibleCard title="Family Members" mb={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            size="small"
            freeSolo
            autoSelect
            options={allFamilyIds}
            value={familyId || ''}
            onChange={(_, val) => {
              if (typeof val !== 'string') { setFamilyId(''); return; }
              const m = val.match(/^Add "(.*)"$/);
              setFamilyId(m ? m[1] : val);
            }}
            onInputChange={(_, val, reason) => {
              // Only commit while typing; `reset` fires on every dropdown close.
              if (reason === 'input') setFamilyId(val || '');
            }}
            filterOptions={(options, state) => {
              const q = (state.inputValue || '').toLowerCase().trim();
              const filtered = q
                ? options.filter((o) => o.toLowerCase().includes(q))
                : options;
              if (q && !options.some((o) => o.toLowerCase() === q)) {
                return [...filtered.slice(0, 50), `Add "${state.inputValue}"`];
              }
              return filtered.slice(0, 50);
            }}
            renderOption={(props, option) => {
              const isAdd = option.startsWith('Add "');
              return (
                <li {...props} key={option}>
                  {isAdd ? <em>{option}</em> : option}
                </li>
              );
            }}
            getOptionLabel={(option) => {
              if (typeof option !== 'string') return '';
              const m = option.match(/^Add "(.*)"$/);
              return m ? m[1] : option;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Family Member ID"
                placeholder="Type a new ID or pick an existing one"
                fullWidth
                helperText="Type any value to create a new family group, or pick an existing ID to join one."
              />
            )}
          />
          {familyId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={familyId} onDelete={() => setFamilyId('')} size="small" />
            </Box>
          )}
          <Autocomplete
            multiple
            size="small"
            options={allProducts}
            getOptionLabel={(o) => o.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={allProducts.filter((p) => selectedFamilyMembers.includes(p.id))}
            onChange={(_, val) => setSelectedFamilyMembers(val.map((v) => v.id))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Additional Family Members"
                helperText="Products that should join this product's family even if they don't share the Family Member ID above. Links are saved bidirectionally — adding B here also makes this product appear in B's family."
              />
            )}
          />
        </Box>
      </CollapsibleCard>

      {/* Installations & Certifications */}
      <CollapsibleCard title="Installations &amp; Certifications" mb={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            multiple
            size="small"
            options={allInstallations}
            getOptionLabel={(o) => o.name}
            value={allInstallations.filter((i) => selectedInstallations.includes(i.id))}
            onChange={(_, val) => setSelectedInstallations(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Installations" />}
          />
          <Autocomplete
            multiple
            size="small"
            options={allCertifications}
            getOptionLabel={(o) => o.name}
            value={allCertifications.filter((c) => selectedCertifications.includes(c.id))}
            onChange={(_, val) => setSelectedCertifications(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Certifications" />}
          />
        </Box>
      </CollapsibleCard>

      {/* ───────── SECTION: Dimensions ───────── */}
      <CollapsibleCard
        title="Dimensions"
        mb={4}
        right={
          <Button size="small" variant="outlined" onClick={() => setCopyDimensionsOpen(true)}>
            Copy from another product
          </Button>
        }
      >
        <DimensionsEditor
          value={productDimensions}
          onChange={setProductDimensions}
          productSlug={product.identifier_value || product.title || ''}
        />
      </CollapsibleCard>

      {/* ───────── SECTION: Pricing (hidden for users without price access) ───────── */}
      {canViewPrice() && (<>
      {/* Active calculations per store */}
      {activeCalculations.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary', mb: 1 }}>
              Active Calculations
            </Typography>
            {[...activeCalculations]
              .sort((a, b) => {
                if (!a.store_id && b.store_id) return -1;
                if (!b.store_id && a.store_id) return 1;
                return (a.shop_domain || '').localeCompare(b.shop_domain || '');
              })
              .map((calc) => {
                const storeLabelText = storeLabel(calc) || 'All stores';
                const scopeLabel = calc._scope === 3
                  ? 'product'
                  : calc._scope === 2
                  ? 'manufacturer'
                  : 'all';
                const mfgNames = (calc.manufacturers || []).map((m) => m.name).join(', ');
                return (
                  <Box
                    key={calc.id}
                    sx={{
                      mb: 1,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 1,
                      '&:last-child': { mb: 0 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip size="small" label={storeLabelText} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
                      <Chip size="small" label={scopeLabel} variant="outlined" sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{calc.name}</Typography>
                      {mfgNames && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>— {mfgNames}</Typography>
                      )}
                    </Box>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {calc.formula}
                    </Box>
                  </Box>
                );
              })}
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
              Variables: mfg_price, add_on (added after), conv_rate, discount, tariff, markup.
              Preview below uses: <b>{primaryCalc?.name}</b>
              {storeLabel(primaryCalc) ? ` (${storeLabel(primaryCalc)})` : ' (All stores)'}.
            </Typography>
          </CardContent>
        </Card>
      )}

      <CollapsibleCard
        title="Pricing"
        mb={2}
        right={
          <Button size="small" variant="outlined" onClick={() => setCopyPricingOpen(true)}>
            Copy from another product
          </Button>
        }
      >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              type="date"
              label="Pricing last updated"
              value={pricingUpdatedAt || ''}
              onChange={(e) => setPricingUpdatedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 220 }}
            />
            <Typography variant="caption" color="text.secondary">
              Auto-stamps to today when the pricing table changes. Override here to back-date or set explicitly.
              Pushed to Shopify as `lastUpdated` inside the product_pricing metafield.
            </Typography>
          </Box>
          <PricingEditor
            value={productPricing}
            onChange={setProductPricing}
            computeListPrice={computePricingListPrice}
          />
      </CollapsibleCard>

      {/* Additional Options for Pricing */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Additional Options for Pricing
            </Typography>
            <Button size="small" startIcon={<AddRounded />} onClick={() => setAdditionalOptions((prev) => [...prev, { name: '', price: 0, add_on: 0 }])}>
              Add Option
            </Button>
          </Box>
          {additionalOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No additional options. Add options like "Padded Seat", "Arm Caps", etc.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {additionalOptions.map((opt, idx) => (
                <Box key={opt.id || `new-${idx}`} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Option Name"
                    value={opt.name}
                    onChange={(e) => setAdditionalOptions((prev) => {
                      const updated = [...prev];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      return updated;
                    })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="MFG Price"
                    type="number"
                    value={opt.price}
                    onChange={(e) => setAdditionalOptions((prev) => {
                      const updated = [...prev];
                      updated[idx] = { ...updated[idx], price: e.target.value };
                      return updated;
                    })}
                    sx={{ width: 140 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                  <TextField
                    size="small"
                    label="Add-on"
                    type="number"
                    value={opt.add_on ?? 0}
                    onChange={(e) => setAdditionalOptions((prev) => {
                      const updated = [...prev];
                      updated[idx] = { ...updated[idx], add_on: e.target.value };
                      return updated;
                    })}
                    sx={{ width: 120 }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                  <IconButton size="small" color="error" onClick={() => setAdditionalOptions((prev) => prev.filter((_, i) => i !== idx))}>
                    <DeleteRounded fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Pricing Preview */}
      {pricingPreview.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Preview
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Item / Option</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>List</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>40% Off</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>50% Off</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>50+10%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Group rows by their first path segment for nicer rendering
                    const sections = {};
                    pricingPreview.forEach((row) => {
                      const groupKey = row.path[0] || 'Items';
                      if (!sections[groupKey]) sections[groupKey] = [];
                      sections[groupKey].push(row);
                    });
                    return Object.entries(sections).flatMap(([groupKey, rows]) => [
                      <TableRow key={`h-${groupKey}`} sx={{ bgcolor: 'grey.100' }}>
                        <TableCell colSpan={5} sx={{ fontStyle: 'italic', fontSize: '0.8rem', fontWeight: 600 }}>
                          {groupKey}
                        </TableCell>
                      </TableRow>,
                      ...rows.map((row, idx) => {
                        const indent = (row.path.length - 1) * 16;
                        const list = row.list_price;
                        const fmt = (v) => v != null ? `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
                        return (
                          <TableRow key={`${groupKey}-${idx}`}>
                            <TableCell sx={{ pl: `${16 + indent}px`, fontSize: '0.85rem' }}>
                              {row.path.slice(1).join(' / ') || row.path[0]}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{fmt(list)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{fmt(list != null ? list * 0.6 : null)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{fmt(list != null ? list * 0.5 : null)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{fmt(list != null ? list * 0.5 * 0.9 : null)}</TableCell>
                          </TableRow>
                        );
                      }),
                    ]);
                  })()}
                </TableBody>
              </Table>
            </TableContainer>

            {gradedPreview.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Graded prices
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  List-side adjustment per fabric grade for {allManufacturers.find((m) => m.id === productManufacturerId)?.name || 'this manufacturer'}.
                  The theme adds the list adjustment to the row's list price to get the final variant price.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Grade</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>MFG price</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>List adjustment</TableCell>
                        {pricingPreview[0] && (
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                            First row example
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {gradedPreview.map((g) => {
                        const fmt = (v) => v != null ? `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
                        const firstRowList = pricingPreview[0]?.list_price;
                        const example = (firstRowList != null && g.list_price != null) ? (firstRowList + g.list_price) : null;
                        return (
                          <TableRow key={g.grade}>
                            <TableCell sx={{ fontSize: '0.85rem' }}>{g.grade}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{fmt(g.mfg_price)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{fmt(g.list_price)}</TableCell>
                            {pricingPreview[0] && (
                              <TableCell align="right" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{fmt(example)}</TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      </>)}

      {/* ───────── SECTION: Other Information ───────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Other Information</Typography>

      <CollapsibleCard title="Materials" mb={2}>
        <Autocomplete
          multiple
          size="small"
          options={allMaterials}
          getOptionLabel={(o) => o.name}
          value={allMaterials.filter((m) => selectedMaterials.includes(m.id))}
          onChange={(_, val) => setSelectedMaterials(val.map((v) => v.id))}
          renderInput={(params) => <TextField {...params} label="Materials" />}
        />
      </CollapsibleCard>

      <CollapsibleCard title="Features" mb={2}>
        <Autocomplete
          multiple
          size="small"
          options={allFeatures}
          getOptionLabel={(o) => o.name}
          value={allFeatures.filter((f) => selectedFeatures.includes(f.id))}
          onChange={(_, val) => setSelectedFeatures(val.map((v) => v.id))}
          renderInput={(params) => <TextField {...params} label="Features" />}
        />
      </CollapsibleCard>

      <CollapsibleCard title="Settings (Usage Areas)" mb={2}>
        <Autocomplete
          multiple
          size="small"
          options={allSettings}
          getOptionLabel={(o) => o.name}
          value={allSettings.filter((s) => selectedSettings.includes(s.id))}
          onChange={(_, val) => setSelectedSettings(val.map((v) => v.id))}
          renderInput={(params) => <TextField {...params} label="Settings (Usage Areas)" />}
        />
      </CollapsibleCard>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField
              size="small"
              label="Asana Inventory Link"
              value={inventoryTracking}
              onChange={(e) => setInventoryTracking(e.target.value)}
              placeholder="e.g. Asana task URL or project ID"
              fullWidth
            />
            <TextField
              size="small"
              label="Lead Time"
              value={leadTime}
              onChange={(e) => setLeadTime(e.target.value)}
              placeholder="e.g. 8-10 Weeks (overrides manufacturer)"
              fullWidth
              helperText="Sent instead of manufacturer's lead time"
            />
            <TextField
              size="small"
              label="MOQ"
              value={moq}
              onChange={(e) => setMoq(e.target.value)}
              placeholder="e.g. 1, 4, 10"
              fullWidth
            />
            <TextField
              size="small"
              label="Stackable"
              value={stackable || ''}
              onChange={(e) => setStackable(e.target.value)}
              fullWidth
            />
          </Box>
        </CardContent>
      </Card>

      {/* Additional Tags — these get appended to auto-tags (materials, etc.) on push to Shopify */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <StoreTabPanel
            label="Additional Tags"
            stores={activeStores}
            defaultValue={additionalTags}
            storeOverrides={getStoreOverrides('additional_tags')}
            useDefaultInitially={false}
            onDefaultChange={(val) => setAdditionalTags(val)}
            onOverrideChange={(storeId, val) => handleOverrideChange('additional_tags', storeId, val)}
            renderEditor={(value, onChange, readOnly) => (
              <TagsInput tags={value || []} onChange={onChange} readOnly={readOnly} />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            These are appended to the auto-generated tags (materials, features, etc.) when pushing to Shopify.
          </Typography>
        </CardContent>
      </Card>

      {/* Extra Information — Right */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Extra Information — Right
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Items shown on the right of the product page. Each item has a title and subitems with image + value.
          </Typography>
          <ExtraInfoEditor value={extraInfoRight} onChange={setExtraInfoRight} variant="right" />
        </CardContent>
      </Card>

      {/* Extra Information — Middle */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Extra Information — Middle
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            A banner image plus items with title and subitems (image + value).
          </Typography>
          <ExtraInfoEditor value={extraInfoMiddle} onChange={setExtraInfoMiddle} variant="middle" />
        </CardContent>
      </Card>

      {/* Match Product Dialog */}
      {/* Copy Dimensions / Pricing from another product. Source's value
          overwrites the local section; save is still required. */}
      <CopyFromProductDialog
        open={copyDimensionsOpen}
        onClose={() => setCopyDimensionsOpen(false)}
        title="Copy dimensions from another product"
        excludeProductId={Number(id)}
        extractField={(src) => src?.dimensions || {}}
        onApply={(value) => setProductDimensions(value || {})}
        helperText="Pick a source product. Its dimensions (size table, fabric consumption, advanced/variants) replace this product's. Save to persist."
      />
      <CopyFromProductDialog
        open={copyPricingOpen}
        onClose={() => setCopyPricingOpen(false)}
        title="Copy pricing from another product"
        excludeProductId={Number(id)}
        extractField={(src) => (src?.pricing && Array.isArray(src.pricing.groups) ? src.pricing : { groups: [] })}
        onApply={(value) => setProductPricing(value)}
        helperText="Pick a source product. Its pricing tree (groups + rows + add-ons) replaces this product's. Save to persist."
      />

      {/* Push store picker — choose exactly which stores to push to. */}
      <Dialog open={pushDialogOpen} onClose={() => setPushDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Push to which stores?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Only the stores you select will be updated. Matched stores are pre-selected.
            Selecting an unmatched store will <b>create a new product</b> there.
          </Typography>
          {activeStores.map((store) => {
            const link = product?.store_statuses?.find((x) => x.store_id === store.id);
            const matched = !!link?.shopify_product_id;
            const checked = pushStoreSel.has(store.id);
            return (
              <Box key={store.id} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={(e) => setPushStoreSel((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(store.id); else next.delete(store.id);
                    return next;
                  })}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {storeLabel(store)}
                  </Typography>
                  <Typography variant="caption" color={matched ? 'success.main' : 'warning.main'}>
                    {matched ? 'Matched — will update' : 'Not matched — will CREATE a new product'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPushDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmPush} disabled={pushStoreSel.size === 0} startIcon={<CloudUploadRounded />}>
            Push to {pushStoreSel.size} store{pushStoreSel.size !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-save: ask whether to push to Shopify now or later. */}
      <Dialog open={postSavePromptOpen} onClose={() => setPostSavePromptOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Push to stores?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Changes are saved. Push them to your Shopify stores now? This creates the product on
            stores that don't have it yet and updates the ones that do.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPostSavePromptOpen(false)}>Later</Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadRounded />}
            onClick={() => {
              setPostSavePromptOpen(false);
              openPushDialog();
            }}
            disabled={syncing}
          >
            Push now
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Match with Shopify Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <TextField
              size="small"
              label="Search by title"
              value={matchQuery}
              onChange={(e) => setMatchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleMatchSearch()}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleMatchSearch}
              disabled={matchSearching || !matchQuery.trim()}
              sx={{ minWidth: 100 }}
            >
              {matchSearching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
          </Box>
          {matchResults.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {matchResults.map((sp) => (
                <Box
                  key={sp.id}
                  sx={{
                    display: 'flex', gap: 2, alignItems: 'center', p: 1.5,
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => handleMatch(sp.id)}
                >
                  {sp.image && (
                    <Box
                      component="img"
                      src={sp.image}
                      alt={sp.title}
                      sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sp.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {sp.id} {sp.status ? `— ${sp.status}` : ''}
                      {sp.variants?.[0]?.sku ? ` — SKU: ${sp.variants[0].sku}` : ''}
                    </Typography>
                  </Box>
                  {matchingProduct && <CircularProgress size={20} />}
                </Box>
              ))}
            </Box>
          )}
          {matchResults.length === 0 && !matchSearching && matchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No results. Try a different search term.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMatchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
   </CollapseAllContext.Provider>
  );
};

export default ProductDetails;
